import { firstValueFrom } from 'rxjs';
import { HttpService } from '../services/http/http.service';
import { HttpErrorResponse } from '@angular/common/http';

export class Api {
  constructor(
    readonly api: string,
    private httpService: HttpService,
  ) {}

  async patch<T, V>(endpoint: string, body?: V, delays = [1000, 2000, 4000]): Promise<T> {
    return this.retryOnNoConnection<T>(
      () => this.makeRequest<T, V>('patch', endpoint, body),
      delays,
    );
  }

  async post<T, V>(endpoint: string, body?: V, delays = [1000, 2000, 4000]): Promise<T> {
    return this.retryOnNoConnection<T>(
      () => this.makeRequest<T, V>('post', endpoint, body),
      delays,
    );
  }

  async get<T>(endpoint: string, delays = [1000, 2000, 4000]): Promise<T> {
    return this.retryOnNoConnection<T>(() => this.makeRequest<T>('get', endpoint), delays);
  }

  static isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse;
  }

  static isNoInternetError(error: unknown): boolean {
    return (
      Api.isHttpErrorResponse(error) && error.status === 0 && error.error instanceof ProgressEvent
    );
  }

  private async retryOnNoConnection<T>(apiCall: () => Promise<T>, delays: number[]): Promise<T> {
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        return await apiCall();
      } catch (error) {
        if (!Api.isHttpErrorResponse(error) || !Api.isNoInternetError(error)) {
          throw error;
        }

        if (attempt < delays.length) {
          await this.sleep(delays[attempt]);
        } else {
          throw error;
        }
      }
    }
    throw new Error(`Failed to call API after ${delays.length + 1} attempts.`);
  }

  private async makeRequest<T, V = undefined>(
    method: 'patch' | 'post' | 'get',
    endpoint: string,
    body?: V,
  ): Promise<T> {
    const url = `${this.api}/${endpoint}`;
    const headers = this.httpService.generateHeaders();

    switch (method) {
      case 'patch':
        return firstValueFrom(this.httpService.https.patch(url, body, { headers })) as Promise<T>;
      case 'post':
        return firstValueFrom(this.httpService.https.post(url, body, { headers })) as Promise<T>;
      case 'get':
        return firstValueFrom(this.httpService.https.get(url, { headers })) as Promise<T>;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
